import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Input,
  Button,
  Typography,
  Avatar,
  Alert,
  Select,
  Dropdown,
  Menu,
  message as antdMessage
} from 'antd';
import { LikeOutlined, DislikeOutlined, SendOutlined, CopyOutlined } from '@ant-design/icons';
import FeedbackModal from '../components/FeedbackModal';
import { useAuth } from '../context/AuthContext';
import '../components/Layout/global.css';
import { generatePDF, generateDOCX } from '../utils/documentGenerator';
import WelcomeScreen from '../components/AIAssistant/WelcomeScreen';
import mermaid from 'mermaid';
import './AIDebuggerpage.css';
import { useAI } from '../context/AIContext';
import { useTheme } from '../context/ThemeContext';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { preprocessMarkdown } from '../utils/formatMarkdownMessage';

const { TextArea } = Input;
const { Text, Title } = Typography;

// Initialize mermaid
mermaid.initialize({
  startOnLoad: false,
  theme: 'default',
  securityLevel: 'loose',
  fontFamily: 'inherit',
  logLevel: 'fatal',
  suppressErrorRendering: true
});

// Code block with copy button
const CodeBlockWithCopy = React.memo(({ content, language }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }, [content]);
  return (
    <div className="ai-markdown-code-block ai-code-block-with-copy">
      <div className="ai-markdown-code-header ai-code-block-header" style={{ justifyContent: language ? 'space-between' : 'flex-end' }}>
        {language && <span className="ai-code-block-lang">{language}</span>}
        <Button
          type="text"
          size="small"
          icon={copied ? null : <CopyOutlined />}
          onClick={handleCopy}
          className="ai-code-block-copy-btn"
        >
          {copied ? 'Copied!' : 'Copy'}
        </Button>
      </div>
      <pre className="ai-markdown-code-pre">
        <code style={{ fontFamily: 'inherit', whiteSpace: 'pre' }}>{content}</code>
      </pre>
    </div>
  );
});

// Memoized input component to prevent re-renders
const ChatInput = React.memo(({ 
  value, 
  onChange, 
  onSend, 
  disabled, 
  inputRef,
  placeholder 
}) => {
  const handleKeyPress = useCallback((e) => {
    if (e.shiftKey) return;
    e.preventDefault();
    onSend();
  }, [onSend]);

  return (
    <TextArea
      ref={inputRef}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      onPressEnter={handleKeyPress}
      className="ai-chat-textarea ai-highlight-input"
      disabled={disabled}
      autoSize={{ minRows: 1, maxRows: 6 }}
    />
  );
});

// Mermaid diagram component
const MermaidDiagram = React.memo(({ chart, index }) => {
  const [svg, setSvg] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const containerRef = useRef(null);
  const renderedRef = useRef(false);
  const renderTimeoutRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    
    // Prevent re-rendering if already rendered successfully
    if (renderedRef.current) {
      return;
    }

    const renderDiagram = async () => {
      if (!chart || !chart.trim()) {
        if (mountedRef.current) {
          setError('Empty diagram');
          setIsLoading(false);
        }
        return;
      }

      try {
        if (mountedRef.current) {
          setIsLoading(true);
        }
        
        const cleanedChart = chart.trim();
        
        // Use a stable ID based on chart content hash to prevent duplicates
        const contentHash = chart.split('').reduce((a, b) => {
          a = ((a << 5) - a) + b.charCodeAt(0);
          return a & a;
        }, 0);
        const id = `mermaid-${Math.abs(contentHash)}-${index}`;
        
        console.log('[Mermaid] Attempting to render diagram:', id);
        
        // Set timeout for rendering (if it takes too long, show error)
        renderTimeoutRef.current = setTimeout(() => {
          if (mountedRef.current && !renderedRef.current) {
            console.warn('[Mermaid] Rendering timeout for:', id);
            setError('Diagram rendering timeout (exceeded 15 seconds)');
            setIsLoading(false);
          }
        }, 15000); 

        let renderResult;
        try {
          // Directly call render without await mermaid.run() to avoid conflicts
          renderResult = await mermaid.render(id, cleanedChart);
          console.log('[Mermaid] Render successful:', id);
        } catch (renderError) {
          console.error('[Mermaid] Render failed:', renderError);
          // Extract meaningful error message
          const errorMsg = renderError.message || renderError.toString();
          
          // Check for common mermaid errors
          if (errorMsg.includes('Parse error')) {
            throw new Error(`Syntax Error: ${errorMsg}`);
          } else if (errorMsg.includes('No diagram type detected')) {
            throw new Error('Invalid diagram type or missing diagram declaration');
          } else {
            throw new Error(errorMsg);
          }
        }
        
        const { svg: renderedSvg } = renderResult;
        
        // Clear timeout on success
        if (renderTimeoutRef.current) {
          clearTimeout(renderTimeoutRef.current);
        }
        
        // Validate SVG content - be less strict
        if (!renderedSvg || renderedSvg.trim().length < 10) {
          console.error('[Mermaid] SVG validation failed, length:', renderedSvg?.length);
          throw new Error('Rendered SVG is empty or invalid');
        }
        
        console.log('[Mermaid] SVG validated successfully, length:', renderedSvg.length);
        
        // Only update state if still mounted
        if (mountedRef.current) {
          setSvg(renderedSvg);
          setError(null);
          setIsLoading(false);
          renderedRef.current = true;
        }
        
        // Clean up error elements from previous failed renders
        setTimeout(() => {
          const errorElements = document.querySelectorAll('[id^="dmermaid-"], .mermaid-error');
          errorElements.forEach(el => {
            if (el && el.parentNode && !el.id.includes(id)) {
              try {
                el.remove();
              } catch (e) {
                // Ignore cleanup errors
              }
            }
          });
        }, 100);
        
      } catch (err) {
        console.error('[Mermaid] Final error handler:', err);
        if (renderTimeoutRef.current) {
          clearTimeout(renderTimeoutRef.current);
        }
        
        // Only update state if still mounted
        if (mountedRef.current) {
          setError(err.message || 'Failed to render diagram');
          setSvg(null);
          setIsLoading(false);
        }
      }
    };

    // Small delay to ensure DOM is ready
    const renderTimeout = setTimeout(() => {
      renderDiagram();
    }, 50);
    
    // Cleanup on unmount
    return () => {
      mountedRef.current = false;
      clearTimeout(renderTimeout);
      if (renderTimeoutRef.current) {
        clearTimeout(renderTimeoutRef.current);
      }
    };
  }, [chart, index]);

  // Show loading indicator while rendering
  if (isLoading && !error) {
    return (
      <div className="ai-mermaid-loading">
        
        Rendering diagram...
      </div>
    );
  }

  // Hide completely if there's an error (don't show anything)
  if (error) {
    return null;
  }

  // Show rendered diagram once ready
  if (svg) {
    return (
      <div 
        ref={containerRef}
        className="ai-mermaid-container"
        dangerouslySetInnerHTML={{ __html: svg }}
      />
    );
  }

  // Fallback (shouldn't normally reach here)
  return null;
}, (prevProps, nextProps) => {
  // Only re-render if chart content actually changes
  return prevProps.chart === nextProps.chart && prevProps.index === nextProps.index;
});

// Thinking Process Component
const ThinkingProcess = React.memo(({ messageId, process, isExpanded, onToggle, isDark = false }) => {
  if (!process) return null;
  
  return (
    <div
      className="ai-thinking-process"
      style={{
        margin: '8px 0',
        padding: '12px',
        background: isDark ? '#161b22' : '#f8f9fa',
        borderRadius: '8px',
        border: isDark ? '1px solid #30363d' : '1px solid #e8e8e8'
      }}
    >
      <div 
        onClick={onToggle}
        className="ai-thinking-header"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          cursor: 'pointer',
          userSelect: 'none'
        }}
      >
        <span style={{ fontSize: '16px' }}>💡</span>
        <Text strong style={{ fontSize: '13px', color: '#52c41a' }}>
          Thinking complete
        </Text>
        <span style={{ marginLeft: 'auto', fontSize: '12px', color: isDark ? '#adbac7' : '#8c8c8c' }} className="ai-thinking-chevron">
          {isExpanded ? '▼' : '▶'}
        </span>
      </div>
      {/* Always in DOM so PDF capture can show it expanded via .pdf-capture-mode */}
      <div
        className="ai-thinking-body"
        style={{
          marginTop: '12px',
          paddingTop: '12px',
          borderTop: isDark ? '1px solid #30363d' : '1px solid #e0e0e0',
          fontSize: '13px',
          color: isDark ? '#adbac7' : '#595959',
          lineHeight: '1.6',
          display: isExpanded ? undefined : 'none'
        }}
      >
        {process}
      </div>
    </div>
  );
});

const FollowUpSuggestions = React.memo(({ suggestions, onClick, isDark = false }) => {
  if (!suggestions || suggestions.length === 0) return null;
  
  const itemBg = isDark ? '#161b22' : '#fafafa';
  const itemColor = isDark ? '#adbac7' : '#262626';
  const itemBorder = isDark ? '#30363d' : '#e8e8e8';
  const hoverBg = isDark ? '#21262d' : '#e6f7ff';
  
  return (
    <div className="ai-follow-up-suggestions" style={{ marginTop: '12px' }}>
      <Text style={{ fontSize: '12px', color: isDark ? '#adbac7' : '#8c8c8c', display: 'block', marginBottom: '8px' }}>
        Related questions:
      </Text>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {suggestions.map((suggestion, idx) => (
          <div
            key={idx}
            className="ai-follow-up-item"
            onClick={() => onClick(suggestion)}
            style={{
              padding: '8px 12px',
              background: itemBg,
              border: `1px solid ${itemBorder}`,
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '13px',
              color: itemColor,
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#1890ff';
              e.currentTarget.style.background = hoverBg;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = itemBorder;
              e.currentTarget.style.background = itemBg;
            }}
          >
            <span style={{ fontSize: '12px' }}>↪</span>
            {suggestion}
          </div>
        ))}
      </div>
    </div>
  );
});

// Message component - moved outside to prevent recreation on every render
//added conversation and onflowupclick props
const Message = React.memo(({ message,  conversation, onFeedbackClick, onFollowUpClick, isDark = false }) => {
  const [copied, setCopied] = useState(false);
  const [feedback, setFeedback] = useState(null); // 'like' or 'dislike'
  const [elapsedTime, setElapsedTime] = useState(0);
  const intervalRef = useRef(null);
  const startTimeRef = useRef(null);
  const isUser = message.type === 'user';
  const isError = message.type === 'error';
  const [thinkingExpanded, setThinkingExpanded] = useState(false);
  const [liveTimer, setLiveTimer] = useState(0);
  const timerRef = useRef(null);
  const messageContentRef = useRef(null);
  const [isGenerating, setIsGenerating] = useState(false); 

  // Calculate elapsed time from message timestamp (persists across navigation)
  useEffect(() => {
    if ((message.isThinking || message.isStreaming) && message.timestamp) {
      const computeElapsed = () => {
        const start = new Date(message.timestamp).getTime();
        return Math.max(0, Math.floor((Date.now() - start) / 1000));
      };
      setLiveTimer(computeElapsed());
      timerRef.current = setInterval(() => setLiveTimer(computeElapsed()), 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      setLiveTimer(0);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [message.isThinking, message.isStreaming, message.timestamp]);
  
  const handleCopy = () => {
    navigator.clipboard.writeText(message.content).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };

  const handleFeedback = (type) => {
    if (feedback === type) {
      setFeedback(null); // Toggle off if clicking the same button
    } else {
      setFeedback(type);
      // Call the parent's feedback handler to open the modal
      if (onFeedbackClick) {
        onFeedbackClick(type, message);
      }
    }
  };
 
//made download option available for chat messages
  const handleDownload = async (format) => {
  const messageIndex = conversation.findIndex(msg => msg.id === message.id);
  let question = 'User Query';

  if (messageIndex > 0) {
    const userMessage = conversation[messageIndex - 1];
    if (userMessage && userMessage.type === 'user') {
      question = userMessage.content;
    }
  }

  const metadata = {
    model: message.model || 'Clairai-Copilot',
    account: 'ClairAI Platform',
    timestamp: message.timestamp
  };

  setIsGenerating(true);
  try {
    if (format === 'pdf') {
      const result = await generatePDF(question, message.content, metadata, {
        contentElement: messageContentRef.current || undefined,
        onWarning: (msg) => antdMessage.info(msg, 4),
        onError: () => antdMessage.error('Failed to generate PDF. Please try again.')
      });
      if (!result?.success) {
        // Error already shown via onError; optionally log or handle
        console.warn('PDF generation returned success: false', result?.error);
      }
    } else if (format === 'docx') {
      await generateDOCX(question, message.content, metadata);
    }
  } catch (error) {
    console.error('Download error:', error);
    antdMessage.error('Failed to generate PDF. Please try again.');
  } finally {
    setIsGenerating(false);
  }
};

  const formatTimestamp = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'pm' : 'am';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  };

  const formatElapsedTime = (seconds) => {
    if (seconds < 60) {
      return `${seconds} sec`;
    } else {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins} min ${secs} sec`;
    }
  };

  // Timer logic for thinking/streaming messages
  useEffect(() => {
    if (message.isThinking || message.isStreaming) {
      // Start timer if not already started
      if (!startTimeRef.current) {
        startTimeRef.current = Date.now();
        setElapsedTime(0);
      }
      
      // Clear any existing interval before creating a new one
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      
      // Start interval to update elapsed time every second
      intervalRef.current = setInterval(() => {
        if (startTimeRef.current) {
          const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
          setElapsedTime(elapsed);
        }
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      startTimeRef.current = null;
      setElapsedTime(0);
    }

    // Cleanup on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [message.isThinking, message.isStreaming]);
  
//Made error more visual appealing
  if (isError) {
    return (
      <div style={{
        display: 'flex',
        gap: '12px',
        width: '100%',
        marginBottom: '12px',
        alignItems: 'flex-start'
      }}>
        <div style={{
          padding: '12px 16px',
          borderRadius: '8px',
          background: 'linear-gradient(135deg, #ff4d4f 0%, #ff7875 100%)',
          color: 'white',
          flex: 1,
          boxShadow: '0 4px 12px rgba(255, 77, 79, 0.15)',
          border: 'none',
          display: 'flex',
          gap: '12px',
          alignItems: 'flex-start'
        }}>
          
          <div>
            <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '2px' }}>
              Something went wrong
            </div>
            <div style={{ fontSize: '13px', opacity: 0.9 }}>
              {message.content.replace('Error: ', '')}
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div id={`msg-${message.id}`} className={`ai-message ${isUser ? 'user' : ''}`}>
      {!isUser && (
        <img
          src="/chatbot (1).svg"
          alt="Clairai-Copilot"
          className={`ai-message-avatar ${message.isThinking ? 'pulsing' : ''}`}
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
      )}
      <div className={`ai-message-bubble ${isUser ? 'user' : ''} ${isError ? 'error' : ''}`}>
        {/* Thinking Process - only for AI messages */}
        {!isUser && message.thinkingProcess && (
        <ThinkingProcess
          messageId={message.id}
          process={message.thinkingProcess}
          isExpanded={thinkingExpanded}
          onToggle={() => setThinkingExpanded(!thinkingExpanded)}
          isDark={isDark}
        />
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
          <Text className={`ai-message-sender ${isUser ? 'user' : ''} ${isError ? 'error' : 'ai'}`}>
            {isUser ? 'You' : isError ? 'Error' : (message.model || 'prometheus-agent')}
            {message.isStreaming && <span style={{ marginLeft: 6, color: '#1890ff' }}>●</span>}
          </Text>
          {!isUser && !isError && message.timestamp && (
          <Text type="secondary" style={{ fontSize: 10 }}>
            {(message.isThinking || message.isStreaming) ? (
              <span style={{ color: '#1890ff' }}>{liveTimer}s</span>
            ) : (
              <>
                {formatTimestamp(message.timestamp)}
                {message.responseTime && ` • ${message.responseTime}s`}
              </>
            )}
          </Text>
          )}
        </div>
        
        {/* Show thinking indicator when streaming or thinking */}
        {/* {(message.isThinking || message.isStreaming) && (
          <div className="ai-thinking-container" style={{ marginBottom: message.isThinking ? 0 : 12 }}>
            <div className="ai-thinking-content" style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#666' }}>
              
              <Text style={{ fontSize: '14px', color: '#666', fontWeight: 500 }}>
                Thinking
              </Text>
              <div className="ai-thinking-dots">
                <span className="ai-thinking-dot"></span>
                <span className="ai-thinking-dot"></span>
                <span className="ai-thinking-dot"></span>
              </div>
              {elapsedTime > 0 && (
                <Text style={{ fontSize: '13px', color: '#8c8c8c', fontWeight: 400, marginLeft: '4px' }}>
                  {formatElapsedTime(elapsedTime)}
                </Text>
              )}
            </div>
          </div>
        )} */}

        {/* Added thinking block so this became duplicate */}
        
        <div ref={messageContentRef} className="ai-message-content">
          {/* {message.isThinking ? null : isError ? (
            <Text style={{ color: 'white' }}>{message.content}</Text>
          ) : (
            <>
              {renderMarkdownToElements(message.content || '')}
              {message.isStreaming && (
                <span style={{ marginLeft: 3, animation: 'blink 1s infinite' }}>▊</span>
              )}
            </>
          )} */}
          {message.isThinking ? (
            <div className="ai-thinking-content" style={{ display: 'flex', alignItems: 'center', gap: '6px', color: isDark ? '#8b949e' : '#666' }}>
              
              <Text style={{ fontSize: '13px', color: isDark ? '#8b949e' : '#666', fontStyle: 'italic' }}>
                Thinking...
              </Text>
            </div>
          ) : isError ? (
            <Text style={{ color: 'white' }}>
              {typeof message.content === 'string'
                ? message.content
                : JSON.stringify(message.content, null, 2)}
            </Text>
          ) : (
<div className="ai-markdown-content" style={{ color: isDark ? '#e6edf3' : '#1a1a1a' }}>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  pre({node, children, ...props}) {
                    // pre wraps a code element in react-markdown block codes
                    const codeNode = node.children.find(child => child.tagName === 'code');
                    if (codeNode) {
                      const className = codeNode.properties?.className || [];
                      const languageClass = className.find(c => String(c).startsWith('language-'));
                      const language = languageClass ? String(languageClass).replace('language-', '') : '';
                      const content = codeNode.children?.[0]?.value || '';

                      if (language === 'mermaid') {
                        return <MermaidDiagram chart={content} index={0} />;
                      }
                      return <CodeBlockWithCopy content={content} language={language} />;
                    }
                    return <pre {...props}>{children}</pre>;
                  },
                  code({node, className, children, ...props}) {
                    // This handles only inline code now since pre handles block code
                    return (
                      <code
                        className={className}
                        style={{
                          background: isDark ? '#21262d' : '#f5f5f5',
                          padding: '2px 6px',
                          borderRadius: '3px',
                          fontFamily: 'monospace',
                          fontSize: '13px',
                          color: isDark ? '#79c0ff' : '#d73a49',
                          border: isDark ? '1px solid #30363d' : '1px solid #e1e4e8'
                        }}
                        {...props}
                      >
                        {children}
                      </code>
                    );
                  },
                  h1({node, children, ...props}) { return <h1 className="ai-markdown-heading-1" {...props}>{children}</h1>; },
                  h2({node, children, ...props}) { return <h2 className="ai-markdown-heading-2" {...props}>{children}</h2>; },
                  h3({node, children, ...props}) { return <h3 className="ai-markdown-heading-3" {...props}>{children}</h3>; },
                  h4({node, children, ...props}) { return <h4 className="ai-markdown-heading-4" {...props}>{children}</h4>; },
                  p({node, children, ...props}) {
                    const child = node.children?.[0];
                    const isSectionTitle =
                      node.children?.length === 1 &&
                      child?.type === 'element' &&
                      child?.tagName === 'strong';
                    const className = isSectionTitle ? 'ai-markdown-section-title' : 'ai-markdown-p';
                    return (
                      <p className={className} style={{ color: isDark ? '#e6edf3' : '#1a1a1a' }} {...props}>
                        {children}
                      </p>
                    );
                  },
                  ul({node, children, ...props}) { return <ul className="ai-markdown-ul" style={{ color: isDark ? '#e6edf3' : '#1a1a1a' }} {...props}>{children}</ul>; },
                  ol({node, children, ...props}) { return <ol className="ai-markdown-ol" style={{ color: isDark ? '#e6edf3' : '#1a1a1a' }} {...props}>{children}</ol>; },
                  li({node, children, ...props}) { return <li className="ai-markdown-li" style={{ color: isDark ? '#e6edf3' : '#1a1a1a' }} {...props}>{children}</li>; },
                  table({node, children, ...props}) {
                    return (
                      <div className="ai-markdown-table-wrap">
                        <table className="ai-markdown-table" style={{ color: isDark ? '#e6edf3' : '#1a1a1a' }} {...props}>{children}</table>
                      </div>
                    );
                  },
                  thead({node, children, ...props}) { return <thead className="ai-markdown-thead" {...props}>{children}</thead>; },
                  tbody({node, children, ...props}) { return <tbody {...props}>{children}</tbody>; },
                  tr({node, children, ...props}) { return <tr className="ai-markdown-tr" {...props}>{children}</tr>; },
                  th({node, children, ...props}) { return <th className="ai-markdown-th" {...props}>{children}</th>; },
                  td({node, children, ...props}) { return <td className="ai-markdown-td" {...props}>{children}</td>; },
                  hr({node, ...props}) { return <hr className="ai-markdown-hr" {...props} />; },
                  strong({node, children, ...props}) { return <strong className="ai-markdown-strong" {...props}>{children}</strong>; },
                  blockquote({node, children, ...props}) {
                    return <blockquote className="ai-markdown-blockquote" {...props}>{children}</blockquote>;
                  },
                }}
              >
                {preprocessMarkdown(
                  typeof message.content === 'string'
                    ? message.content
                    : JSON.stringify(message.content, null, 2)
                )}
              </ReactMarkdown>
            </div>
          )}
          {message.isStreaming && !message.isThinking && (
            <span style={{ marginLeft: 3, animation: 'blink 1s infinite' }}>▊</span>
          )}
        </div>
        {!isError && !message.isThinking && message.content && !isUser && (
          <div style={{ 
            display: 'flex', 
            gap: '4px', 
            marginTop: '8px', 
            paddingTop: '8px', 
            borderTop: isDark ? '1px solid #30363d' : '1px solid #f0f0f0',
            justifyContent: 'flex-end'
          }}>
            <Button
              type="text"
              size="small"onClick={handleCopy}
              style={{ 
                padding: '4px 8px', 
                height: 'auto', 
                fontSize: '12px',
                color: copied ? '#52c41a' : (isDark ? '#adbac7' : '#8c8c8c')
              }}
              title={copied ? 'Copied!' : 'Copy'}
            >
              {copied ? 'Copied' : 'Copy'}
            </Button>
            <>
              <Button
                  type="text"
                  icon={<LikeOutlined />}
                  size="small"
                  onClick={() => handleFeedback('like')}
                  style={{ 
                    padding: '4px 8px', 
                    height: 'auto', 
                    fontSize: '12px',
                    color: feedback === 'like' ? '#52c41a' : (isDark ? '#adbac7' : '#8c8c8c')
                  }}
                  title="Good response"
                />
                <Button
                  type="text"
                  icon={<DislikeOutlined />}
                  size="small"
                  onClick={() => handleFeedback('dislike')}
                  style={{ 
                    padding: '4px 8px', 
                    height: 'auto', 
                    fontSize: '12px',
                    color: feedback === 'dislike' ? '#ff4d4f' : (isDark ? '#adbac7' : '#8c8c8c')
                  }}
                  title="Bad response"
                />
                    <Dropdown
      menu={{
        items: [
          {
            key: 'pdf',
            label: '📄 Download as PDF',
            onClick: () => handleDownload('pdf'),
            disabled: isGenerating
          },
          {
            key: 'docx',
            label: '📝 Download as DOCX',
            onClick: () => handleDownload('docx'),
            disabled: isGenerating
          }
        ]
      }}
      placement="bottomLeft"
      disabled={isGenerating}
    >
  <Button
    type="text"
    size="small"
    loading={isGenerating}
    disabled={isGenerating}
    style={{
      padding: '4px 8px',
      height: 'auto',
      fontSize: '12px',
      color: '#8c8c8c',
      display: 'flex',
      alignItems: 'center',
      gap: '4px'
    }}
    title={isGenerating ? 'Generating document...' : 'Download'}
  >
    {isGenerating ? 'Generating...' : '📥 Download'}
  </Button>
    </Dropdown>
            </>
          </div>
        )}

      {/* Follow-up Suggestions - only for AI messages */}
        {!isUser && !isError && !message.isThinking && message.followUpSuggestions && (
          <FollowUpSuggestions
            suggestions={message.followUpSuggestions}
            onClick={onFollowUpClick}
            isDark={isDark}
          />
        )}
        </div>
      {isUser && (
        <Avatar className="ai-message-avatar user"
          style={{ backgroundColor: '#52c41a', boxShadow: '0 2px 6px rgba(82, 196, 26, 0.3)' }}
        />
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  // Return true if props are equal (skip re-render), false if different (re-render)
  return (
    prevProps.message.id === nextProps.message.id &&
    prevProps.message.content === nextProps.message.content &&
    prevProps.message.isStreaming === nextProps.message.isStreaming &&
    prevProps.message.isThinking === nextProps.message.isThinking &&
    prevProps.message.type === nextProps.message.type &&
    prevProps.message.model === nextProps.message.model &&
    prevProps.onFollowUpClick === nextProps.onFollowUpClick &&
    prevProps.conversation === nextProps.conversation &&
    prevProps.isDark === nextProps.isDark
  );
});

const AIDebugger = () => {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  // const [query, setQuery] = useState('');
  // const [conversation, setConversation] = useState([]);
  // const [loading, setLoading] = useState(false);
  // const [error, setError] = useState(null);
  //have comented the local state so that global state from useAIChat can be used
  const [query, setQuery] = useState('');
  const { 
    conversation, 
    status, 
    startTask, 
    abort,
    clearConversation,
    clearBadge,
    messageMetadata,
    updateMessage,
    activeModel,
    setActiveModel 
  } = useAI();
  const [error, setError] = useState(null);
  const [models, setModels] = useState([]);

  // const [abortController, setAbortController] = useState(null);
  const [thinkingProcess, setThinkingProcess] = useState({});
  const [followUpSuggestions, setFollowUpSuggestions] = useState({});

  const generateFollowUps = useCallback((content) => {
    const extractedQuestions = [];
    const aiGeneratedSuggestions = [];

    // Extract questions from AI response
    const lines = content.split('\n');
    let inFollowUpSection = false;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      const lowerLine = line.toLowerCase();

      // Detect follow-up section
      if (lowerLine.includes('follow-up questions') || 
          lowerLine.includes('follow-up:') || 
          lowerLine.includes('related questions') ||
          lowerLine.includes('related:') || 
          lowerLine.includes('next steps:')) {
        inFollowUpSection = true;
        continue;
      }

      // Extract questions from bullet points
      if (inFollowUpSection) {
        if (line.startsWith('- ') || line.startsWith('* ')) {
          const question = line.replace(/^[*-]\s+/, '').trim();
          if (question) {
            extractedQuestions.push(question);
          }
        } else if (line && !line.startsWith(' ')) {
          inFollowUpSection = false;
        }
      }
    }

    // Add contextual suggestions based on content
    const contentLower = content.toLowerCase();
    
    if (contentLower.includes('error')) {
      aiGeneratedSuggestions.push('Show me recent error patterns');
      aiGeneratedSuggestions.push('What are the most common error types?');
    } else if (contentLower.includes('cost')) {
      aiGeneratedSuggestions.push('How can I optimize AWS costs?');
      aiGeneratedSuggestions.push('Show me cost breakdown by service');
    } else if (contentLower.includes('performance')) {
      aiGeneratedSuggestions.push('What are the performance bottlenecks?');
      aiGeneratedSuggestions.push('Show me latency metrics');
    } else {
      aiGeneratedSuggestions.push('Show me related metrics');
      aiGeneratedSuggestions.push('What are the current health indicators?');
    }

    // Combine and return max 3 suggestions
    return [...extractedQuestions, ...aiGeneratedSuggestions].slice(0, 3);
  }, []);
  // Generate follow-ups when AI finishes responding
  useEffect(() => {
    if (status === 'finished' && conversation.length > 0) {
      const lastMessage = conversation[conversation.length - 1];
      
      // Only process AI messages without follow-ups
      if (lastMessage.type === 'ai' && !lastMessage.followUpSuggestions) {
        const followUps = generateFollowUps(lastMessage.content);
        
        // Update the message with follow-ups
        updateMessage(lastMessage.id, { followUpSuggestions: followUps });
      }
    }
  }, [status, conversation, generateFollowUps, updateMessage]);
  // Follow-up Suggestions Component
    useEffect(() => {
    // Clear badge when user opens AI page
    clearBadge();
  }, [clearBadge]);
  
  // Generate or retrieve sessionId from sessionStorage (persists across page refreshes)
  // Session ID represents a conversation session - a period of interaction between user and AI
  // Format: chat-{username}-{YYYYMMDD-HHMMSS}-{shortId}
  // Example: chat-john-20241119-143022-a3k9x
  // const generateSessionId = useCallback(() => {
  //   // Get user identifier for meaningful session ID
  //   const userIdentifier = user?.username || user?.client_id || 'guest';
    
  //   // Create readable timestamp: YYYYMMDD-HHMMSS
  //   const now = new Date();
  //   const year = now.getFullYear();
  //   const month = String(now.getMonth() + 1).padStart(2, '0');
  //   const day = String(now.getDate()).padStart(2, '0');
  //   const hours = String(now.getHours()).padStart(2, '0');
  //   const minutes = String(now.getMinutes()).padStart(2, '0');
  //   const seconds = String(now.getSeconds()).padStart(2, '0');
  //   const timestamp = `${year}${month}${day}-${hours}${minutes}${seconds}`;
    
  //   // Short unique identifier (6 characters)
  //   const shortId = Math.random().toString(36).substr(2, 6);
    
  //   // Create meaningful session ID
  //   return `chat-${userIdentifier}-${timestamp}-${shortId}`;
  // }, [user?.username, user?.client_id]);

  // const [sessionId, setSessionId] = useState(() => {
  //   const stored = sessionStorage.getItem('ai_chat_session_id');
  //   const storedUser = sessionStorage.getItem('ai_chat_user_for_session');
  //   const currentUser = user?.username || user?.client_id || 'guest';
    
  //   // If stored session exists and user matches, use it
  //   if (stored && storedUser === currentUser) {
  //     return stored;
  //   }
    
  //   // Otherwise, generate new session ID
  //   const newSessionId = generateSessionId();
  //   sessionStorage.setItem('ai_chat_session_id', newSessionId);
  //   sessionStorage.setItem('ai_chat_user_for_session', currentUser);
  //   return newSessionId;
  // });

  // Update session ID if user changes
  // useEffect(() => {
  //   const currentUser = user?.username || user?.client_id || 'guest';
  //   const storedUser = sessionStorage.getItem('ai_chat_user_for_session');
    
  //   if (storedUser !== currentUser) {
  //     const newSessionId = generateSessionId();
  //     setSessionId(newSessionId);
  //     sessionStorage.setItem('ai_chat_session_id', newSessionId);
  //     sessionStorage.setItem('ai_chat_user_for_session', currentUser);
  //   }
  // }, [user?.username, user?.client_id, generateSessionId]);
  
  // Use authenticated user ID if available, otherwise generate a temporary one
  // const [userId] = useState(() => {
  //   // Priority: username > client_id > email > sub > generated ID
  //   if (user?.username) {
  //     return user.username;
  //   }
  //   if (user?.client_id) {
  //     return user.client_id;
  //   }
  //   if (user?.email) {
  //     return user.email;
  //   }
  //   if (user?.sub) {
  //     return user.sub;
  //   }
  //   // Generate temporary user ID and store in sessionStorage
  //   const stored = sessionStorage.getItem('ai_chat_user_id');
  //   if (stored) return stored;
  //   const newUserId = `user-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  //   sessionStorage.setItem('ai_chat_user_id', newUserId);
  //   return newUserId;
  // });
  
  const [feedbackModalVisible, setFeedbackModalVisible] = useState(false);
  const [feedbackData, setFeedbackData] = useState({
    question: '',
    response: '',
    feedbackType: 'positive',
  });
  // AI Agent backend base URL (without /v1 suffix for direct endpoint control)
  // const API_BASE_URL = process.env.REACT_APP_AI_API_BASE_URL || 'https://assistant.clairai.cloud';
  // const DEFAULT_COMPLETION_OPTS = { max_tokens: 1000, stream: true, temperature: 0.7 };

  const inputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  // const isStreamingRef = useRef(false);
  // const lastMessageCountRef = useRef(0);
  const queryRef = useRef(query);
  
  // Keep queryRef in sync with query state
  useEffect(() => {
    queryRef.current = query;
  }, [query]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // useEffect(() => {
  //   // Only scroll when new messages are added (not during streaming content updates)
  //   const currentMessageCount = conversation.length;
  //   const hasNewMessage = currentMessageCount !== lastMessageCountRef.current;
  //   lastMessageCountRef.current = currentMessageCount;

  //   if (conversation.length > 0 && chatContainerRef.current && (hasNewMessage)) {
  //     // Only scroll when not streaming or when a new message is added
  //     requestAnimationFrame(() => {
  //       if (chatContainerRef.current ) {
  //         chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
  //       }
  //     });
  //   }
  // }, [conversation.length]); // Only depend on message count, not content

  // useEffect(() => {
  //   const init = async () => {
  //     try {
  //       fetch(`${API_BASE_URL}/health`).catch(() => {});
  //       try {
  //         const res = await fetch(`${API_BASE_URL}/v1/models`, {
  //           headers: {
  //             'X-Openwebui-User-Id': userId,
  //             'X-Openwebui-Chat-Id': sessionId
  //           }
  //         });
  //         if (res.ok) {
  //           const data = await res.json();
  //           // Handle different response formats
  //           let modelList = [];
  //           if (Array.isArray(data)) {
  //             modelList = data;
  //           } else if (data?.data && Array.isArray(data.data)) {
  //             modelList = data.data;
  //           } else if (data?.model) {
  //             modelList = [{ id: data.model, name: data.model }];
  //           } else if (data?.id || data?.name) {
  //             modelList = [data];
  //           } else if (typeof data === 'string') {
  //             modelList = [{ id: data, name: data }];
  //           }
            
  //           if (modelList.length > 0) {
  //             setModels(modelList);
  //             const first = modelList[0]?.id || modelList[0]?.name || modelList[0];
  //             setActiveModel(first || null);
  //           }
  //         }
  //       } catch (e) {
  //         console.error('Failed to fetch models:', e);
  //         // Don't set fallback, let it remain empty or use the default
  //       }
  //     } catch (e) {}
  //   };
  //   init();
  // }, [API_BASE_URL, userId, sessionId]);

  // Memoized handlers to prevent unnecessary re-renders
  
  useEffect(() => {
      // Scroll to bottom when conversation changes
      if (conversation.length > 0 && chatContainerRef.current) {
        requestAnimationFrame(() => {
          if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
          }
        });
      }
    }, [conversation.length]);
  const handleQueryChange = useCallback((e) => {
    setQuery(e.target.value);
  }, []);

  const decodeUnicodeContent = (text) => {
    if (typeof text !== 'string') return text;
    return text.replace(/\\u([\da-fA-F]{4})/g, (match, grp) => {
      return String.fromCharCode(parseInt(grp, 16));
    });
  };

  const parseStreamingResponse = async (response, onChunk, onComplete, controller) => {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let fullContent = '';

    try {
      while (true) {
        if (controller.signal.aborted) {
          reader.cancel();
          throw new Error('Request was aborted');
        }
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const dataStr = line.slice(6);
            if (dataStr === '[DONE]') {
              onComplete(fullContent);
              return;
            }
            try {
              const data = JSON.parse(dataStr);
              if (data?.choices?.[0]?.delta?.content) {
                const content = decodeUnicodeContent(data.choices[0].delta.content);
                fullContent += content;
                onChunk(content, fullContent);
              }
            } catch (parseError) {
              console.warn('Failed to parse SSE chunk:', parseError);
            }
          }
        }
      }
      onComplete(fullContent);
    } catch (error) {
      if (error.name === 'AbortError' || error.message === 'Request was aborted') {
        onComplete(fullContent, true);
      } else {
        throw error;
      }
    }
  };

  // const handleSendQuery = useCallback(async () => {
  //   if (!queryRef.current.trim()) return;
  //   const currentQuery = queryRef.current;
  //   setLoading(true);
  //   setError(null);
  //   const controller = new AbortController();
  //   setAbortController(controller);
  //   const userMessage = {
  //     id: Date.now(),
  //     type: 'user',
  //     content: query,
  //     timestamp: new Date().toISOString()
  //   };
  //   setConversation(prev => [...prev, userMessage]);
  //   // Scroll will be handled by useEffect when message count changes
  //   setQuery('');
  //   const aiMessageId = Date.now() + 1;
  //   const aiMessage = {
  //     id: aiMessageId,
  //     type: 'ai',
  //     content: 'Thinking...',
  //     timestamp: new Date().toISOString(),
  //     model: activeModel || 'prometheus-agent',
  //     isStreaming: false,
  //     isThinking: true
  //   };
  //   setConversation(prev => [...prev, aiMessage]);
  //   try {
  //     let response = await fetch(`${API_BASE_URL}/v1/chat/completions`, {
  //       method: 'POST',
  //       headers: {
  //         'Content-Type': 'application/json',
  //         'accept': 'application/json',
  //         'X-Openwebui-User-Id': userId,
  //         'X-Openwebui-Chat-Id': sessionId
  //       },
  //       body: JSON.stringify({
  //         model: activeModel || 'prometheus-agent',
  //         messages: [
  //           { role: 'system', content: 'You are a helpful cloud observability assistant.' },
  //           { role: 'user', content: currentQuery.trim() }
  //         ],
  //         ...DEFAULT_COMPLETION_OPTS
  //       }),
  //       signal: controller.signal
  //     });
  //     if (response && response.status === 404) {
  //       response = await fetch(`${API_BASE_URL}/v1/api/v1/query`, {
  //         method: 'POST',
  //         headers: {
  //           'Content-Type': 'application/json',
  //           'accept': 'application/json',
  //           'X-Openwebui-User-Id': userId,
  //           'X-Openwebui-Chat-Id': sessionId
  //         },
  //         body: JSON.stringify({ query: currentQuery.trim() }),
  //         signal: controller.signal
  //       });
  //     }
  //     if (!response.ok) {
  //       const errorText = await response.text().catch(() => '');
  //       let errorData = {};
  //       try { errorData = errorText ? JSON.parse(errorText) : {}; } catch {}
  //       const errorMessage = errorData.error || errorData.message || errorData.detail || errorText || `HTTP ${response.status}`;
  //       throw new Error(`HTTP Error (${response.status}): ${errorMessage}`);
  //     }
  //     const contentType = response.headers.get('content-type');
  //     const isStreaming = contentType && contentType.includes('text/event-stream');
  //     console.log('Response content-type:', contentType, 'isStreaming:', isStreaming);
  //     if (isStreaming) {
  //       isStreamingRef.current = true;
  //       let fullContent = '';
        
  //       // Update UI in real-time as content streams in
  //       await parseStreamingResponse(
  //         response,
  //         (chunk, content) => {
  //           // Update UI with streaming content
  //           fullContent = content;
  //           setConversation(prev => prev.map(msg =>
  //             msg.id === aiMessageId
  //               ? {
  //                 ...msg,
  //                 content: content,
  //                 isStreaming: true,
  //                 isThinking: false
  //               }
  //               : msg
  //           ));
  //         },
  //         (finalContent, wasAborted = false) => {
  //           isStreamingRef.current = false;
  //           // Final update when streaming is complete
  //           setConversation(prev => prev.map(msg =>
  //             msg.id === aiMessageId
  //               ? {
  //                 ...msg,
  //                 content: finalContent + (wasAborted ? '\n\n_[Response was interrupted]_' : ''),
  //                 isStreaming: false,
  //                 isThinking: false
  //               }
  //               : msg
  //           ));
  //           setLoading(false);
  //           setAbortController(null);
  //           // Scroll to bottom
  //           if (chatContainerRef.current) {
  //             chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
  //           }
  //         },
  //         controller
  //       );
  //     } else {
  //       const text = await response.text();
  //       let data = {};
  //       try {
  //         data = text ? JSON.parse(text) : {};
  //       } catch (parseError) {
  //         if (typeof text === 'string' && text.trim()) {
  //           const decodedContent = decodeUnicodeContent(text.trim());
  //           setConversation(prev => prev.map(msg =>
  //             msg.id === aiMessageId
  //               ? { ...msg, content: decodedContent, isStreaming: false, isThinking: false }
  //               : msg
  //           ));
  //           setLoading(false);
  //           setAbortController(null);
  //           return;
  //         }
  //         throw new Error('Invalid response format: not JSON and not plain text');
  //       }
  //       let aiText = '';
  //       if (Array.isArray(data?.choices) && data.choices[0]?.message?.content) {
  //         aiText = data.choices[0].message.content;
  //       } else if (typeof data?.response === 'string') {
  //         aiText = data.response;
  //       } else if (typeof data?.output_text === 'string') {
  //         aiText = data.output_text;
  //       } else if (typeof data?.content === 'string') {
  //         aiText = data.content;
  //       } else {
  //         const msg = data?.error || data?.detail || data?.message || 'Response format not recognized';
  //         throw new Error(`Invalid response format: ${msg}`);
  //       }
  //       if (!aiText || !aiText.trim()) {
  //         throw new Error('Response content is empty');
  //       }
  //       const decodedContent = decodeUnicodeContent(aiText);
  //       setConversation(prev => prev.map(msg =>
  //         msg.id === aiMessageId
  //           ? { ...msg, content: decodedContent, isStreaming: false, isThinking: false }
  //           : msg
  //       ));
  //       setLoading(false);
  //       setAbortController(null);
  //       // Scroll will be handled by useEffect
  //     }
  //   } catch (err) {
  //     if (err.name === 'AbortError' || err.message === 'Request was aborted') {
  //       setConversation(prev => prev.map(msg =>
  //         msg.id === aiMessageId
  //           ? { ...msg, content: '_[Response was interrupted]_', isStreaming: false, isThinking: false }
  //           : msg
  //       ));
  //     } else {
  //       setError(err.message);
  //       setConversation(prev => prev.filter(msg => msg.id !== aiMessageId));
  //       const errorMessage = {
  //         id: Date.now() + 'error',
  //         type: 'error',
  //         content: `Error: ${err.message}`,
  //         timestamp: new Date().toISOString()
  //       };
  //       setConversation(prev => [...prev, errorMessage]);
  //     }
  //   } finally {
  //     setLoading(false);
  //     setAbortController(null);
  //   }
  // }, [activeModel, userId, sessionId, API_BASE_URL, DEFAULT_COMPLETION_OPTS]);

// Use startTask from Context; also tolerate being called as an event handler.
const handleSendQuery = useCallback((followUpQuestion = null, customModel = null) => {
  // If called directly as an onClick handler, first arg will be a MouseEvent.
  let safeFollowUp = followUpQuestion;
  if (safeFollowUp && typeof safeFollowUp === 'object' && 'preventDefault' in safeFollowUp) {
    safeFollowUp = null;
  }

  const raw = safeFollowUp ?? query ?? '';
  const currentQuery = typeof raw === 'string' ? raw : (raw?.query ?? raw?.text ?? String(raw));
  const trimmed = currentQuery.trim();
  if (!trimmed) return;
  
  // Use the model parameter if provided, otherwise use activeModel
  const modelToUse = customModel || activeModel || 'prometheus-agent';
  
  // Call startTask from Context (BackgroundRunner handles the rest)
  startTask(trimmed, modelToUse);
  
    if (customModel) {
    setActiveModel(customModel);
  }
  // Clear input if not a follow-up
  if (!followUpQuestion) {
    setQuery('');
  }
}, [query, activeModel, startTask]);

  const handleFollowUpClick = useCallback((question) => {
    handleSendQuery(question);
  }, [handleSendQuery]);

  // Store latest handleSendQuery in ref to avoid stale closures
  const handleSendQueryRef = useRef(handleSendQuery);
  useEffect(() => {
    handleSendQueryRef.current = handleSendQuery;
  }, [handleSendQuery]);

  // When navigated from Alerts page with prompt + model, auto-send once (ref avoids duplicate in Strict Mode)
  const hasProcessedInitialPromptRef = useRef(false);
  const lastProcessedPromptRef = useRef(null);
  
  useEffect(() => {
    const prompt = location.state?.prompt;
    if (!prompt || typeof prompt !== 'string') {
      // Reset flag when there's no prompt (e.g., after navigation clears state)
      hasProcessedInitialPromptRef.current = false;
      lastProcessedPromptRef.current = null;
      return;
    }
    
    // Check if we've already processed this exact prompt
    if (hasProcessedInitialPromptRef.current && lastProcessedPromptRef.current === prompt) {
      return;
    }
    
    // Set flag immediately to prevent duplicate sends (before any async operations)
    hasProcessedInitialPromptRef.current = true;
    lastProcessedPromptRef.current = prompt;
    
    const model = location.state?.model || 'auto v1';
    // Use ref to call latest handleSendQuery without including it in dependencies
    handleSendQueryRef.current(prompt.trim(), model);
    
    // Clear state immediately to prevent re-processing
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state?.prompt, location.pathname, navigate]);

  // const clearConversation = useCallback(() => {
  //   setConversation([]);
  //   setError(null);
  // }, []);

  //using global state so have to remove this block
//   const clearConversation = useCallback(() => {
//   setConversation([]);
//   setError(null);
  
//   // Generate new session ID
//   const newSessionId = generateSessionId();
//   setSessionId(newSessionId);
//   sessionStorage.setItem('ai_chat_session_id', newSessionId);
  
//   const currentUser = user?.username || user?.client_id || 'guest';
//   sessionStorage.setItem('ai_chat_user_for_session', currentUser);
// }, [generateSessionId, user?.username, user?.client_id]);

  const handleFeedbackClick = useCallback((feedbackType, message) => {
    // Find the user's question that corresponds to this AI response
    const messageIndex = conversation.findIndex(msg => msg.id === message.id);
    if (messageIndex > 0) {
      // The previous message should be the user's question
      const userMessage = conversation[messageIndex - 1];
      if (userMessage && userMessage.type === 'user') {
        setFeedbackData({
          question: userMessage.content,
          response: message.content,
          feedbackType: feedbackType === 'like' ? 'positive' : 'negative',
        });
        setFeedbackModalVisible(true);
      }
    }
  }, [conversation]);

  // const handleStopGeneration = useCallback(() => {
  //   if (abortController) {
  //     abortController.abort();
  //     setLoading(false);
  //     setAbortController(null);
  //   }
  // }, [abortController]);

  const handleStopGeneration = useCallback(() => {
    abort();
  }, [abort]);

useEffect(() => {
  const API_BASE_URL = process.env.REACT_APP_AI_API_BASE_URL || 'https://assistant.clairai.cloud';
  
  const fetchModels = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/models`);
      if (response.ok) {
        const data = await response.json();
        
        // Handle different response formats
        let modelList = [];
        if (Array.isArray(data)) {
          modelList = data;
        } else if (data?.data && Array.isArray(data.data)) {
          modelList = data.data;
        }
        
        if (modelList.length > 0) {
          setModels(modelList);
        } else {
          // Fallback to your known models
          setModels([
            { id: 'auto-v1', name: 'Auto V1' },
            { id: 'rca-v1', name: 'RCA V1' },
            { id: 'finops', name: 'FinOps' }
          ]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch models:', error);
      // Fallback to your known models
      setModels([
        { id: 'auto-v1', name: 'Auto V1' },
        { id: 'rca-v1', name: 'RCA V1' },
        { id: 'finops', name: 'FinOps' }
      ]);
      setActiveModel('auto-v1');
    }
  };
  
  fetchModels();
}, []);

const handleModelChange = useCallback((val) => {
  console.log('🔄 Dropdown changed to:', val);
  setActiveModel(val);
  console.log('✅ setActiveModel called with:', val);
}, [setActiveModel]);

const modelOptions = useMemo(() => {
  const source = (Array.isArray(models) && models.length > 0)
    ? models
    : (activeModel ? [{ id: activeModel, name: activeModel }] : []);

  const modelMeta = {
    'auto-v1': { title: 'Auto V1', desc: 'Balanced assistant for most requests' },
    'auto v1': { title: 'Auto V1', desc: 'Balanced assistant for most requests' },
    'rca-v1': { title: 'RCA V1', desc: 'Root cause and incident troubleshooting' },
    'finops': { title: 'FinOps', desc: 'Cost and usage analysis' },
  };

  return source.map((m) => {
    const raw = (m?.id || m?.name || String(m)).trim();
    const key = raw.toLowerCase();
    const meta = modelMeta[key] || {
      title: raw,
      desc: 'Specialized agent',
    };

    return {
      value: raw,
      label: (
        <div className="ai-agent-option">
          <div className="ai-agent-option__title">{meta.title}</div>
          <div className="ai-agent-option__desc">{meta.desc}</div>
        </div>
      ),
      searchText: `${meta.title} ${meta.desc} ${raw}`,
    };
  });
}, [models, activeModel]);

const welcomeGreeting = useMemo(() => {
  const rawName =
    user?.username ||
    user?.name ||
    (typeof user?.email === 'string' ? user.email.split('@')[0] : '') ||
    '';
  const firstName = String(rawName).trim().split(/\s+/)[0] || 'there';
  return `Hi ${firstName}, hope you're doing well today.`;
}, [user]);

  const isWelcome = conversation.length === 0;
  const renderComposer = () => (
    <div
      className={`ai-chat-input-area${isWelcome ? ' ai-chat-input-area--welcome ai-chat-input-area--welcome-middle' : ''}`}
      style={{
        background: isDark ? '#161b22' : undefined,
        borderTop: isDark ? '1px solid #30363d' : undefined,
      }}
    >
      <div className="ai-chat-input-wrapper">
        <div className="ai-chat-input-row">
          <ChatInput
            inputRef={inputRef}
            placeholder='Try: "Why did API latency spike in the last 30 min?" or "Show recent errors for pipeline service"'
            value={query}
            onChange={handleQueryChange}
            onSend={handleSendQuery}
            disabled={status === "running"} 
          />
          {status === "running" ? (
            <Button
              onClick={handleStopGeneration}
              type="primary"
              danger
              className="ai-chat-send-button"
              title="Stop generating"
            >
              Stop
            </Button>
          ) : (
            <Button
              onClick={handleSendQuery}
              disabled={!(typeof query === 'string' && query.trim())}
              type="primary"
              className="ai-chat-send-button"
              icon={<SendOutlined />}
              title="Send message"
            />
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div
      className={`ai-chat-container${isWelcome ? ' ai-chat-container--welcome' : ''}`}
      style={{ background: isDark ? '#0d1117' : undefined }}
    >
      <div
        className="ai-chat-header"
        style={{
          background: isDark && !isWelcome ? '#161b22' : undefined,
          borderBottom: isDark ? '1px solid #30363d' : undefined,
        }}
      >
        <div className="ai-chat-header-content">
          {(activeModel) && (
            <div className="ai-agent-select-wrap">
              <span className="ai-agent-select-label">MODEL</span>
              <Select
                size="middle"
                value={activeModel}
                onChange={handleModelChange}
                className="ai-agent-select"
                dropdownClassName="ai-agent-select-dropdown"
                popupClassName="ai-agent-select-dropdown"
                showSearch
                optionFilterProp="searchText"
                options={modelOptions}
              />
            </div>
          )}
        </div>
        <Button onClick={clearConversation}
          disabled={conversation.length === 0}
          type="text"
          className="ai-chat-clear-button"
          size="small"
          style={{ 
            fontSize: 11, 
            height: 20, 
            padding: '0 6px',
            color: isDark ? '#adbac7' : undefined,
          }}
        >
          Clear
        </Button>
      </div>
      {/* //commented these changes */}
      {/* <div className="ai-chat-body" ref={chatContainerRef}>
        {conversation.length === 0 && (
          <div className="ai-chat-welcome">
            <div className="ai-chat-welcome-icon">
              <img
                src="/chatbot (1).svg"
                alt="AI Chatbot"
                className="ai-chat-welcome-logo"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <Title level={2} className="ai-welcome-title">
                Clairai-Copilot
              </Title>
            </div>
            <Text type="secondary" className="ai-welcome-text">
              Start a conversation by asking about your AWS infrastructure, logs, metrics, costs, or any observability questions.
            </Text>
            <div className="ai-chat-input-area ai-chat-input-area-centered">
              <div className="ai-chat-input-container">
                <div className="ai-chat-input-row">
                  <ChatInput
                    inputRef={inputRef}
                    placeholder="Ask about your AWS infrastructure, logs, metrics, costs, or any observability questions..."
                    value={query}
                    onChange={handleQueryChange}
                    onSend={handleSendQuery}
                    disabled={status === "running"}
                  />
                  {status === "running" ? (
                    <Button
                      onClick={handleStopGeneration}
                      type="primary"
                      danger
                      className="ai-chat-send-button"title="Stop generating"
                    />
                  ) : (
                    <Button
                      onClick={handleSendQuery}
                      disabled={!(typeof query === 'string' && query.trim())}
                      type="primary"
                      className="ai-chat-send-button"title="Send message"
                    />
                  )}
                </div>
                {error && (
                  <Alert message="Error" description={error} type="error"style={{ marginTop: 2 }} />
                )}
              </div>
            </div>
          </div>
        )}
        {conversation.length > 0 && (
          <>
            <div className="ai-chat-messages">
              {conversation.map((message) => (
                <Message 
                  key={message.id} 
                  message={message} 
                  conversation={conversation}
                  onFeedbackClick={handleFeedbackClick}
                  onFollowUpClick={handleFollowUpClick}
                  isDark={isDark}
                />
              ))}
              <div ref={messagesEndRef} className="ai-chat-messages-scroll-anchor" />
            </div>
          </>
        )}
      </div>
      {conversation.length > 0 && (
        <div className="ai-chat-input-area">
          <div className="ai-chat-input-container">
            <div className="ai-chat-input-row">
              <ChatInput
                inputRef={inputRef}
                placeholder="Ask about your AWS infrastructure, logs, metrics, costs, or any observability questions..."
                value={query}
                onChange={handleQueryChange}
                onSend={handleSendQuery}
                disabled={status === "running"}
              />
              {status === "running" ? (
                <Button
                  onClick={handleStopGeneration}
                  type="primary"
                  danger
                  className="ai-chat-send-button"title="Stop generating"
                />
              ) : (
                <Button
                  onClick={handleSendQuery}
                  disabled={!(typeof query === 'string' && query.trim())}
                  type="primary"
                  className="ai-chat-send-button"title="Send message"
                />
              )}
        </div>
      </div>

{/* Error have been designed and and declared at top */}
            {/* </div>
            {error && (
              <Alert message="Error" description={error} type="error"style={{ marginTop: 2 }} />
            )} */}
          {/* </div>
        </div> */}
      {/* )} */}

      {/* <div className="ai-chat-body" ref={chatContainerRef}>
        {conversation.length === 0 && (
          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            gap: '16px',
            padding: '16px'
          }}>
            <img
              src="/chatbot (1).svg"
              alt="AI Chatbot"
              style={{ width: '48px', height: '48px' }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div>
              <Title level={3} style={{ margin: 0, color: '#1890ff' }}>
                Clairai-Copilot
              </Title>
              <Text type="secondary" style={{ fontSize: '13px' }}>
                Start a conversation about your AWS infrastructure
              </Text>
            </div>
          </div>
        )} */}
        <div
          className={`ai-chat-body${isWelcome ? ' ai-chat-body--welcome' : ''}`}
          ref={chatContainerRef}
          style={{ background: isDark ? '#0d1117' : undefined }}
        >
          {isWelcome ? (
            <WelcomeScreen
              onQuickAction={handleSendQuery}
              greetingText={welcomeGreeting}
              middleComposer={renderComposer()}
            />
          ) : (
            <>
              {/* {conversation.map((message) => (
                <Message
                  key={message.id}
                  message={message}
                  conversation={conversation}
                  onFeedbackClick={handleFeedbackClick}
                  onFollowUpClick={handleFollowUpClick}
                />
              ))} */}
              {conversation.map((message) => (
                <Message
                  key={message.id}
                  message={{
                    ...message,
                    thinkingProcess: messageMetadata[message.id]?.thinkingProcess || message.thinkingProcess,
                    followUpSuggestions: messageMetadata[message.id]?.followUpSuggestions || message.followUpSuggestions,
                    responseTime: messageMetadata[message.id]?.responseTime || message.responseTime
                  }}
                  conversation={conversation}
                  onFeedbackClick={handleFeedbackClick}
                  onFollowUpClick={handleFollowUpClick}
                  isDark={isDark}
                />
              ))}
             </>
          )}
        {/* Moved to top */}
        {/* {conversation.length > 0 && conversation.map((message) => (
          <Message
            key={message.id}
            message={message}
            conversation={conversation}
            onFeedbackClick={handleFeedbackClick}
            onFollowUpClick={handleFollowUpClick}
          />
        ))} */}
      </div>

      {!isWelcome && renderComposer()}

      <FeedbackModal
        visible={feedbackModalVisible}
        onCancel={() => setFeedbackModalVisible(false)}
        onSuccess={() => {
          // Feedback submitted successfully
        }}
        question={feedbackData.question}
        response={feedbackData.response}
        feedbackType={feedbackData.feedbackType}
        // userId={userId}
        // sessionId={sessionId}
      />

    </div>

  );
};

export default AIDebugger;